import React, { useEffect, useState } from "react";
import { Box, Typography, TextField, Button, Chip, Stack, Paper, Tooltip } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import { useNavigate } from "react-router-dom";
import { authHeader, getSession } from "../auth";

// La liste des catégories vient du serveur (pas du localStorage) : ça
// garantit que chronometreur, colour et paralleltimer voient tous la même
// liste, même sur des navigateurs/appareils différents.
export default function ParallelHub() {
    const [categories, setCategories] = useState([]);
    const [presence, setPresence] = useState({}); // { [category]: { username, page } }
    const [newCategory, setNewCategory] = useState("");
    const [error, setError] = useState("");
    // Catégorie en cours d'archivage — désactive juste le bouton concerné
    // le temps de l'appel, sans bloquer le reste de la liste.
    const [archivingCategory, setArchivingCategory] = useState(null);
    const navigate = useNavigate();
    const session = getSession();
    const role = session?.role;

    const loadCategories = () => {
        fetch("/api/parallel-categories", { headers: { ...authHeader() } })
            .then(async (r) => {
                if (!r.ok) {
                    const data = await r.json().catch(() => ({}));
                    throw new Error(data.error || `HTTP ${r.status}`);
                }
                return r.json();
            })
            .then(setCategories)
            .catch((err) => setError(err.message));
    };

    const loadPresence = () => {
        fetch("/api/presence", { headers: { ...authHeader() } })
            .then((r) => (r.ok ? r.json() : {}))
            .then(setPresence)
            .catch(() => {});
    };

    useEffect(() => {
        loadCategories();
        loadPresence();
        // L'activité est éphémère (expire après ~20s côté serveur) : on
        // réinterroge régulièrement pour que les pastilles jaunes restent à jour.
        const interval = setInterval(loadPresence, 6000);
        return () => clearInterval(interval);
    }, []);

    const links = (category) => {
        const items = [];
        if (role === "chronometreur") {
            items.push({ label: "Import athlètes", path: `/athletes-import/${category}` });
            items.push({ label: "Import qualifications", path: `/qualifications-import/${category}` });
            items.push({ label: "Couleurs", path: `/bibs/${category}` });
            items.push({ label: "Bracket", path: `/brackets/${category}` });
        } else if (role === "colour") {
            items.push({ label: "Couleurs", path: `/bibs/${category}` });
        } else if (role === "paralleltimer") {
            items.push({ label: "Bracket", path: `/brackets/${category}` });
        }
        return items;
    };

    // Créer une nouvelle catégorie = simplement y naviguer pour importer sa
    // première liste d'athlètes ; elle apparaîtra ensuite dans la liste
    // ci-dessus dès que quelque chose y aura été importé.
    const startNewCategory = () => {
        const name = newCategory.trim();
        if (!name) return;
        navigate(`/athletes-import/${encodeURIComponent(name)}`);
    };

    // Génère (ou régénère) le bracket à partir des qualifications importées.
    // Modifié le 30.09.2026 : la taille du tableau et le nombre de
    // qualifiés suivent le règlement FIS Télémark (Art. 2200 : 16 dames /
    // 32 hommes, réduit selon le nombre de concurrents). Le serveur calcule
    // ce plan (/api/finals/bracket-plan), affiché ici avant confirmation.
    // Destructif si un bracket existait déjà (couleurs/gagnants perdus).
    const fetchPlan = async (category, gender) => {
        const query = gender ? `?gender=${gender}` : "";
        const res = await fetch(`/api/finals/bracket-plan/${encodeURIComponent(category)}${query}`, {
            headers: { ...authHeader() },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        return data;
    };

    const generateBracket = async (category) => {
        setError("");
        try {
            let plan = await fetchPlan(category);
            if (!plan.imported) {
                setError(`Aucune qualification importée pour "${category}".`);
                return;
            }

            // Genre non déductible (ni config FIS, ni nom de catégorie) :
            // il détermine la taille maximale du tableau.
            if (!plan.gender) {
                const answer = (window.prompt(
                    `Genre de la catégorie "${category}" ?\n\nW = dames (tableau de 16 max)\nM = hommes (tableau de 32 max)`,
                    "W"
                ) || "").trim().toUpperCase();
                if (answer !== "W" && answer !== "M") return;
                plan = await fetchPlan(category, answer);
            }

            const lines = [
                `Générer le bracket pour "${category}" (${plan.gender === "W" ? "dames" : "hommes"}) ?`,
                "",
                `${plan.participants} classés en qualification` +
                (plan.notClassifiedInQualification ? ` (+ ${plan.notClassifiedInQualification} DNS/DNF/DSQ)` : ""),
                `Règlement : ${plan.rule}`,
                `→ ${plan.qualified} qualifiés pour les finales` +
                (plan.byes ? `, ${plan.byes} bye(s)` : ""),
                plan.notQualified
                    ? `→ ${plan.notQualified} non qualifiés (DNQ dans l'export FIS)`
                    : "→ aucun non qualifié",
                "",
                "Si un bracket existe déjà pour cette catégorie, il sera remplacé — " +
                "les couleurs et gagnants déjà enregistrés seront perdus.",
            ];
            if (!window.confirm(lines.join("\n"))) return;

            const res = await fetch(`/api/finals/generate/${encodeURIComponent(category)}`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeader() },
                body: JSON.stringify({ gender: plan.gender }),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `HTTP ${res.status}`);
                return;
            }

            loadCategories();
        } catch (err) {
            setError(err.message);
        }
    };

    // Archive la catégorie : elle disparaît de cette liste, mais toutes
    // les données de chronométrage (bracket, résultats, écarts,
    // photofinish, décisions du jury) sont conservées côté serveur — ce
    // n'est pas une suppression.
    const archiveCategory = async (category) => {
        const confirmed = window.confirm(
            `Archiver "${category}" ?\n\n` +
            `La course sera retirée de cette liste, mais toutes les données de ` +
            `chronométrage (bracket, résultats, écarts, photofinish, décisions du ` +
            `jury) seront conservées et resteront consultables depuis les archives.`
        );
        if (!confirmed) return;

        setError("");
        setArchivingCategory(category);
        try {
            const res = await fetch(`/api/finals/archive/${category}`, {
                method: "POST",
                headers: { ...authHeader() },
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `HTTP ${res.status}`);
                return;
            }

            loadCategories();
        } catch (err) {
            setError(err.message);
        } finally {
            setArchivingCategory(null);
        }
    };

    return (
        <Box p={3}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                <Typography variant="h4">Courses parallèles</Typography>
                {role === "chronometreur" && (
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<Inventory2Icon />}
                            onClick={() => navigate("/parallel-archives")}
                        >
                            Archives
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<SettingsIcon />}
                            onClick={() => navigate("/parallel-config")}
                        >
                            Config impression
                        </Button>
                    </Box>
                )}
            </Box>
            <Typography variant="body2" color="text.secondary" mb={3}>
                Jusqu'à 4 (ou plus) courses parallèles peuvent tourner en même temps, chacune avec sa
                propre catégorie, sa liste d'athlètes, ses qualifications et son bracket, totalement
                indépendants les uns des autres.
                <br />
                🟢 = étape déjà réalisée &nbsp;•&nbsp; 🟡 = quelqu'un y travaille en ce moment
            </Typography>

            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    Erreur de chargement : {error}
                </Typography>
            )}

            {role === "chronometreur" && (
                <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
                    <TextField
                        label="Nouvelle catégorie (ex: women1, men1...)"
                        size="small"
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && startNewCategory()}
                        sx={{ minWidth: 320 }}
                    />
                    <Button variant="contained" onClick={startNewCategory}>
                        Créer / importer
                    </Button>
                </Box>
            )}

            {categories.length === 0 ? (
                <Typography color="text.secondary">
                    Aucune catégorie active pour l'instant.
                </Typography>
            ) : (
                <Stack spacing={2}>
                    {categories.map((cat) => {
                        const active = presence[cat.category];
                        return (
                            <Paper key={cat.category} sx={{ p: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    <Typography variant="h6">{cat.category}</Typography>
                                    {active && (
                                        <Tooltip
                                            title={`${active.username} travaille sur "${active.page}" en ce moment`}
                                        >
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: "50%",
                                                    backgroundColor: "#fbc02d",
                                                    boxShadow: "0 0 6px #fbc02d",
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </Box>

                                {/* Statut des 3 étapes */}
                                <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                                    <Chip
                                        size="small"
                                        label="Athlètes"
                                        sx={{
                                            backgroundColor: cat.hasAthletes ? "#c8e6c9" : "#eeeeee",
                                            color: cat.hasAthletes ? "#2e7d32" : "text.secondary",
                                        }}
                                    />
                                    <Chip
                                        size="small"
                                        label="Qualifications"
                                        sx={{
                                            backgroundColor: cat.hasQualifications ? "#c8e6c9" : "#eeeeee",
                                            color: cat.hasQualifications ? "#2e7d32" : "text.secondary",
                                        }}
                                    />
                                    <Chip
                                        size="small"
                                        label="Bracket"
                                        sx={{
                                            backgroundColor: cat.hasBracket ? "#c8e6c9" : "#eeeeee",
                                            color: cat.hasBracket ? "#2e7d32" : "text.secondary",
                                        }}
                                    />
                                </Box>

                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                                    {links(cat.category).map((item) => (
                                        <Chip
                                            key={item.path}
                                            label={item.label}
                                            clickable
                                            onClick={() => navigate(item.path)}
                                        />
                                    ))}
                                    {cat.hasBracket && (
                                        <Chip
                                            label="🔴 Live"
                                            clickable
                                            color="error"
                                            variant="outlined"
                                            onClick={() =>
                                                window.open(`/parallel-live/${cat.category}`, "_blank")
                                            }
                                        />
                                    )}
                                    {role === "chronometreur" && (
                                        <>
                                            <Chip
                                                label="⚙️ Config impression"
                                                clickable
                                                onClick={() => navigate(`/parallel-config/${cat.category}`)}
                                            />
                                            <Chip
                                                label="📤 Export FIS"
                                                clickable
                                                onClick={() => navigate(`/parallel-fis/${cat.category}`)}
                                            />
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="warning"
                                                onClick={() => generateBracket(cat.category)}
                                            >
                                                Générer / régénérer le bracket
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="error"
                                                startIcon={<Inventory2Icon fontSize="small" />}
                                                disabled={archivingCategory === cat.category}
                                                onClick={() => archiveCategory(cat.category)}
                                            >
                                                {archivingCategory === cat.category
                                                    ? "Archivage…"
                                                    : "Archiver"}
                                            </Button>
                                        </>
                                    )}
                                </Box>
                            </Paper>
                        );
                    })}
                </Stack>
            )}
        </Box>
    );
}